import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, FileText, ArrowRight, Compass, Calendar, RotateCcw } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isAdminRole } from "@/lib/roles";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";

const Dashboard = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(true);
  const [hasProgress, setHasProgress] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/");
    if (!loading && isAdminRole(user)) navigate("/admin");
  }, [user, loading, navigate]);

  useEffect(() => {
    const load = async () => {
      try {
        const [a, p] = await Promise.all([api.get("/assessments"), api.get("/progress")]);
        setItems(a.data);
        setHasProgress(!!(p.data && p.data.answers && Object.keys(p.data.answers).length > 0));
      } catch (e) { console.error("Dashboard load failed", e); } finally { setBusy(false); }
    };
    if (user) load();
  }, [user]);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />
      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-12">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
          <p className="font-body text-sm text-slate-500">Welcome back{user?.name ? "," : ""}</p>
          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl text-slate-900">
            {user?.name?.split(" ")[0] || "Explorer"}
          </h1>
        </motion.div>

        {/* Resume banner */}
        {hasProgress && (
          <button onClick={() => navigate("/assessment")} data-testid="resume-banner"
            className="mt-8 w-full text-left rounded-2xl border border-amber-200 bg-amber-50 p-5 flex items-center justify-between gap-4 hover:border-amber-300 transition-colors">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-400/20 flex items-center justify-center">
                <RotateCcw className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="font-heading font-bold text-amber-900">You have an assessment in progress</p>
                <p className="font-body text-sm text-amber-800/70">Pick up right where you left off.</p>
              </div>
            </div>
            <span className="font-heading font-bold text-amber-700 flex items-center gap-1">Resume <ArrowRight className="w-4 h-4" /></span>
          </button>
        )}

        {/* Start new (hidden for code users who already completed their single assessment) */}
        {(user?.auth_type !== "code" || items.length === 0) ? (
          <div className="mt-8 rounded-2xl bg-primary p-8 relative overflow-hidden flex flex-col sm:flex-row sm:items-center justify-between gap-6">
            <div className="relative z-10">
              <h2 className="font-heading font-extrabold text-2xl text-white">Take a new assessment</h2>
              <p className="font-body text-indigo-100 mt-2 max-w-md">Cognitive · Aptitude · DISC · Holland — about 15 minutes.</p>
            </div>
            <Button onClick={() => navigate("/assessment")} data-testid="start-assessment-btn"
                    className="relative z-10 rounded-xl h-12 px-6 bg-white text-primary hover:bg-indigo-50 font-heading font-bold transition-transform hover:scale-[1.02]">
              <Plus className="w-4 h-4 mr-1" /> Start now
            </Button>
            <Compass className="absolute -right-6 -bottom-8 w-48 h-48 text-white/10" />
          </div>
        ) : (
          <div className="mt-8 rounded-2xl border border-emerald-200 bg-emerald-50 p-6" data-testid="assessment-complete-note">
            <h2 className="font-heading font-extrabold text-xl text-emerald-900">You've completed your assessment</h2>
            <p className="font-body text-emerald-800/80 mt-1">Your report is ready below — open it anytime, download the PDF, or share it.</p>
          </div>
        )}

        {/* History */}
        <div className="mt-12">
          <h3 className="font-heading font-bold text-xl text-slate-900 mb-5">Your reports</h3>
          {busy ? (
            <p className="font-body text-slate-400">Loading…</p>
          ) : items.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 p-12 text-center" data-testid="empty-reports">
              <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="font-body text-slate-500">No reports yet. Take your first assessment above.</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {items.map((it, i) => (
                <motion.button
                  key={it.assessment_id}
                  initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                  onClick={() => navigate(`/report/${it.assessment_id}`)}
                  className="text-left bg-white rounded-2xl border border-slate-200 p-6 hover:-translate-y-1 hover:border-primary/40 transition-[transform,border-color]"
                  data-testid={`report-card-${i}`}>
                  <div className="flex items-center justify-between">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-primary/10 font-heading font-bold text-xs text-primary">
                      Holland {it.scores?.holland?.code}
                    </span>
                    <ArrowRight className="w-4 h-4 text-slate-400" />
                  </div>
                  <p className="font-heading font-bold text-slate-900 mt-4 line-clamp-2">
                    {it.report?.headline || "Career Alignment Report"}
                  </p>
                  <div className="flex items-center gap-4 mt-4 font-body text-xs text-slate-500">
                    <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{new Date(it.created_at).toLocaleDateString()}</span>
                    <span>DISC: {it.scores?.disc?.primary}</span>
                  </div>
                </motion.button>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
